// Manage the "From" identities outbound mail can be sent as.
//
// Postmark accepts any address on a verified domain, so adding an identity
// here needs no dashboard step — but the address must stay on one of those
// domains, which the API enforces on save rather than letting the send fail
// after recipients are already resolved.

import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import client from '../../lib/feathers';
import EmailEditor from '../../components/EmailEditor';

const BLANK = {
  label: '', fromName: '', fromEmail: '', replyTo: '', signatureHtml: '', kind: 'personal', isDefault: false,
};

export default function EmailSenders() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await client.service('admin/email-sender-identity').find({ query: {} });
      setRows(r.data || []);
    } catch (err) {
      toast.error(err.message || 'Failed to load senders');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = { ...editing };
    delete payload.id;
    delete payload.createdAt;
    delete payload.updatedAt;
    delete payload.createdByUserId;
    delete payload.isActive;
    try {
      if (editing.id) {
        await client.service('admin/email-sender-identity').patch(editing.id, payload);
      } else {
        await client.service('admin/email-sender-identity').create(payload);
      }
      toast.success('Saved');
      setEditing(null);
      load();
    } catch (err) {
      // The domain rule comes back with the allowed list in the message —
      // surface it verbatim rather than a generic failure.
      toast.error(err.message || 'Failed to save');
    }
  };

  const retire = async (row) => {
    try {
      await client.service('admin/email-sender-identity').remove(row.id);
      toast.success(`${row.label} retired`);
      load();
    } catch (err) {
      toast.error(err.message || 'Failed to retire');
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-2xl font-semibold text-gray-900">Email Senders</h1>
          <button
            type="button"
            onClick={() => setEditing({ ...BLANK })}
            className="px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
          >
            Add sender
          </button>
        </div>
        <p className="text-sm text-gray-500 mb-6">
          Who outbound mail comes from. <strong>Personal</strong> identities are offered on 1:1
          sends and can carry a signature; <strong>system</strong> identities are what bulk
          campaigns send as. Addresses must be on a verified sending domain.
        </p>

        {loading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : (
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['Label', 'From', 'Reply-to', 'Kind', '', ''].map((h) => (
                    <th key={h} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((r) => (
                  <tr key={r.id} className={r.isActive ? '' : 'opacity-50'}>
                    <td className="px-3 py-2 text-sm text-gray-900">
                      {r.label}
                      {r.isDefault && r.isActive && (
                        <span className="ml-2 px-1.5 py-0.5 text-xs rounded bg-blue-100 text-blue-800">default</span>
                      )}
                      {!r.isActive && <span className="ml-2 text-xs text-gray-500">retired</span>}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-700">{r.fromName} &lt;{r.fromEmail}&gt;</td>
                    <td className="px-3 py-2 text-sm text-gray-700">{r.replyTo || <span className="text-gray-400">—</span>}</td>
                    <td className="px-3 py-2 text-sm text-gray-700">{r.kind}</td>
                    <td className="px-3 py-2 text-sm">
                      <button type="button" onClick={() => setEditing({ ...r })} className="text-blue-600 hover:underline">Edit</button>
                    </td>
                    <td className="px-3 py-2 text-sm">
                      {r.isActive && (
                        <button type="button" onClick={() => retire(r)} className="text-red-600 hover:underline">Retire</button>
                      )}
                    </td>
                  </tr>
                ))}
                {!rows.length && (
                  <tr><td colSpan="6" className="px-3 py-6 text-center text-sm text-gray-500">No senders yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {editing && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-auto p-6 space-y-4">
              <h2 className="text-lg font-semibold text-gray-900">{editing.id ? 'Edit sender' : 'Add sender'}</h2>

              {[
                ['label', 'Label', 'Shown in the From dropdown'],
                ['fromName', 'From name', 'e.g. Simeon Bateman'],
                ['fromEmail', 'From address', 'Must be on a verified sending domain'],
                ['replyTo', 'Reply-to (optional)', 'Where replies actually land — may be any address'],
              ].map(([field, label, hint]) => (
                <div key={field}>
                  <label htmlFor={`sender-${field}`} className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                  <input
                    id={`sender-${field}`}
                    type="text"
                    value={editing[field] || ''}
                    onChange={(e) => setEditing({ ...editing, [field]: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">{hint}</p>
                </div>
              ))}

              <div>
                <label htmlFor="sender-kind" className="block text-sm font-medium text-gray-700 mb-1">Kind</label>
                <select
                  id="sender-kind"
                  value={editing.kind}
                  onChange={(e) => setEditing({ ...editing, kind: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                >
                  <option value="personal">Personal — 1:1 sends, carries a signature</option>
                  <option value="system">System — bulk campaigns</option>
                </select>
              </div>

              <label htmlFor="sender-default" className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  id="sender-default"
                  type="checkbox"
                  checked={!!editing.isDefault}
                  onChange={(e) => setEditing({ ...editing, isDefault: e.target.checked })}
                />
                Default for this kind
              </label>

              {editing.kind === 'personal' && (
                <div>
                  <span className="block text-sm font-medium text-gray-700 mb-1">Signature</span>
                  <EmailEditor
                    value={editing.signatureHtml || ''}
                    onChange={(html) => setEditing({ ...editing, signatureHtml: html })}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Appended to 1:1 sends. Bulk sends get the unsubscribe footer instead — the two never stack.
                  </p>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setEditing(null)} className="px-3 py-2 text-sm border border-gray-300 rounded-md">Cancel</button>
                <button type="button" onClick={save} className="px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700">Save</button>
              </div>
            </div>
          </div>
        )}
      </div>
  );
}
